import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Snackbar,
  IconButton,
  Stack,
  Chip,
  Avatar,
  Divider,
  Modal,
  Fade,
  Backdrop,
  Paper,
  Tooltip,
  Zoom,
  useTheme,
  useMediaQuery,
  CardActionArea,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  CreditScore as CreditScoreIcon,
  Star as StarIcon,
  LocalOffer as OfferIcon,
  PointOfSale as PointIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cardService, Bank, CreditCard, UserCard } from '../services/cards';

const operatingCategoryLabel = (cat?: Bank['operating_category']): string => {
  if (cat === 'TRADITIONAL') return 'Traditional bank';
  if (cat === 'NON_TRADITIONAL') return 'Non-traditional bank';
  if (cat === 'UNKNOWN') return 'Unknown category';
  return '—';
};

// Only these 5 banks are allowed for card selection
const ALLOWED_BANK_CODES = ['REPUBLIC', 'RBC', 'FIRST_CITIZENS', 'SCOTIA', 'JMMB'];

// Premium card background gradients
const cardBackgrounds = {
  'Visa': 'linear-gradient(135deg, #1a1f71 0%, #0a4b8c 100%)',
  'Mastercard': 'linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)',
  'American Express': 'linear-gradient(135deg, #006fcf 0%, #ae8f6f 100%)',
  'Discover': 'linear-gradient(135deg, #ff6600 0%, #ff9433 100%)',
  'default': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
};

// Premium bank-specific gradients with patterns
const bankGradients: { [key: string]: string } = {
  'REPUBLIC': 'linear-gradient(135deg, #003366 0%, #0066cc 100%)',
  'RBC': 'linear-gradient(135deg, #0066b3 0%, #003f6b 100%)',
  'FIRST_CITIZENS': 'linear-gradient(135deg, #8b1e41 0%, #c62d4c 100%)',
  'SCOTIA': 'linear-gradient(135deg, #e30613 0%, #a0040e 100%)',
  'JMMB': 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
  'default': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
};

// Card pattern overlays for premium look
const cardPatterns = {
  'default': 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 30%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.1) 0%, transparent 30%)',
  'visa': 'radial-gradient(circle at 10% 20%, rgba(255,255,255,0.15) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(255,255,255,0.15) 0%, transparent 40%)',
  'mastercard': 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.12) 0%, transparent 35%), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.12) 0%, transparent 35%)',
};

const Cards: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedBank, setSelectedBank] = useState<number | null>(null);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [cardNumberLastFour, setCardNumberLastFour] = useState('');
  const [expiry, setExpiry] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: banks = [] } = useQuery<Bank[]>({
    queryKey: ['banks'],
    queryFn: () => cardService.getBanks(),
  });
  
  // Only show the 5 allowed traditional banks
  const filteredBanks = banks.filter(bank => 
    bank.code && ALLOWED_BANK_CODES.includes(bank.code.toUpperCase())
  );

  const { data: cardsData = [], isLoading: cardsLoading, error: cardsError } = useQuery<CreditCard[]>({
    queryKey: ['available-cards', selectedBank],
    queryFn: () => cardService.getCards({ bank: selectedBank, show_all: false }),
    enabled: !!selectedBank,
    retry: 2,
  });

  const availableCards = (cardsData || []).filter((card) => {
    if (!card) return false;
    if (!card.name || typeof card.name !== 'string' || card.name.trim() === '') return false;
    if (!card.bank) return false;
    if (card.is_active === false) return false;
    if (!(card as any).scraping_url || !(card as any).last_scraped) return false;
    return true;
  });

  const { data: userCards = [] } = useQuery<UserCard[]>({
    queryKey: ['userCards'],
    queryFn: cardService.getUserCards,
  });

  const addCardMutation = useMutation({
    mutationFn: ({
      cardId,
      lastFour,
      expiryDate,
    }: {
      cardId: number;
      lastFour: string;
      expiryDate: string;
    }) => cardService.addUserCard(cardId, lastFour, expiryDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCards'] });
      setSnackbar({ open: true, message: 'Card added successfully!', severity: 'success' });
      handleCloseModal();
    },
    onError: (error: any) => {
      const data = error.response?.data || {};
      const fieldError =
        data.expiry_date?.[0] ||
        data.card_number_last4?.[0] ||
        data.detail ||
        'Failed to add card';
      setSnackbar({
        open: true,
        message: fieldError,
        severity: 'error',
      });
    },
  });

  const deleteCardMutation = useMutation({
    mutationFn: (userCardId: number) => cardService.deleteUserCard(userCardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCards'] });
      setSnackbar({ open: true, message: 'Card removed successfully!', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to remove card',
        severity: 'error',
      });
    },
  });

  const handleOpenModal = () => {
    setAddCardModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddCardModalOpen(false);
    setSelectedBank(null);
    setSelectedCard(null);
    setCardNumberLastFour('');
    setExpiry('');
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length >= 2) {
      // Add slash after first two digits
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }
    
    // Limit to 5 characters (MM/YY)
    if (value.length <= 5) {
      setExpiry(value);
    }
  };

  const handleAddCard = () => {
    if (!selectedCard) {
      setSnackbar({ open: true, message: 'Please select a card', severity: 'error' });
      return;
    }
    if (!cardNumberLastFour || cardNumberLastFour.length !== 4 || !/^\d{4}$/.test(cardNumberLastFour)) {
      setSnackbar({ open: true, message: 'Please enter last 4 digits of card number', severity: 'error' });
      return;
    }
    if (!expiry || !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) {
      setSnackbar({
        open: true,
        message: 'Please enter expiry in MM/YY format (e.g. 12/28)',
        severity: 'error',
      });
      return;
    }
    addCardMutation.mutate({ cardId: selectedCard, lastFour: cardNumberLastFour, expiryDate: expiry });
  };

  const handleDeleteCard = (userCardId: number) => {
    if (window.confirm('Are you sure you want to remove this card?')) {
      deleteCardMutation.mutate(userCardId);
    }
  };

  const getCardBackground = (card: CreditCard) => {
    if (card.image_url) return `url(${card.image_url})`;
    
    // Get bank code from card
    const bankObj = card.bank as Bank;
    const bankCode = bankObj?.code?.toUpperCase();
    if (bankCode && bankGradients[bankCode]) {
      return bankGradients[bankCode];
    }
    
    // Fallback to card type gradient
    return cardBackgrounds[card.card_type as keyof typeof cardBackgrounds] || cardBackgrounds.default;
  };

  const getCardPattern = (cardType: string) => {
    switch(cardType?.toLowerCase()) {
      case 'visa':
        return cardPatterns.visa;
      case 'mastercard':
        return cardPatterns.mastercard;
      default:
        return cardPatterns.default;
    }
  };

  const getCardLogo = (cardType: string) => {
    switch(cardType?.toLowerCase()) {
      case 'visa':
        return 'VISA';
      case 'mastercard':
        return 'MasterCard';
      case 'american express':
        return 'AMEX';
      default:
        return '💳';
    }
  };

  // Calculate statistics
  const totalCards = userCards.length;
  const cashbackCards = userCards.filter(c => c.card?.cashback_rate && c.card.cashback_rate > 0).length;
  const rewardsCards = userCards.filter(c => c.card?.reward_points_rate && c.card.reward_points_rate > 0).length;
  const averageCashback = userCards.length > 0 
    ? (userCards.reduce((acc, c) => acc + (c.card?.cashback_rate || 0), 0) / userCards.length).toFixed(1)
    : '0';

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Hero Header with Statistics */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
          borderRadius: { xs: 2, md: 4 },
          p: { xs: 3, md: 4 },
          mb: { xs: 3, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header Title and Add Button */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  width: { xs: 60, md: 70 },
                  height: { xs: 60, md: 70 },
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid rgba(255,255,255,0.3)',
                }}
              >
                <CreditCardIcon sx={{ color: 'white', fontSize: { xs: 36, md: 42 } }} />
              </Box>
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  fontWeight={900} 
                  sx={{ 
                    color: 'white', 
                    mb: 0.5, 
                    fontSize: { xs: '2rem', md: '2.5rem' } 
                  }}
                >
                  My Cards
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)', 
                    fontWeight: 400, 
                    fontSize: { xs: '0.9rem', md: '1.1rem' } 
                  }}
                >
                  Manage your credit cards and optimize your rewards
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Add New Card
            </Button>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)',
                  textAlign: 'center',
                }}
              >
                <CreditScoreIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white', lineHeight: 1.2 }}>
                  {totalCards}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  Total Cards
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)',
                  textAlign: 'center',
                }}
              >
                <PaymentIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white', lineHeight: 1.2 }}>
                  {cashbackCards}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  Cashback Cards
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)',
                  textAlign: 'center',
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white', lineHeight: 1.2 }}>
                  {rewardsCards}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  Rewards Cards
                </Typography>
              </Paper>
            </Grid>
            
            {/* <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)',
                  textAlign: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 32, color: 'white', mb: 1 }} />
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white', lineHeight: 1.2 }}>
                  {averageCashback}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  Avg. Cashback
                </Typography>
              </Paper>
            </Grid> */}
          </Grid>
        </Box>
      </Box>

      {/* Your Cards Section - Full Width */}
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
          border: '1px solid rgba(102, 126, 234, 0.1)',
          width: '100%',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Your Cards
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userCards.length} card{userCards.length !== 1 ? 's' : ''} linked to your account
              </Typography>
            </Box>
            <Chip
              icon={<CheckIcon />}
              label={`${userCards.length} Active`}
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                fontWeight: 600,
                px: 2,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>

          {userCards.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                borderRadius: 3,
                bgcolor: 'rgba(102, 126, 234, 0.05)',
              }}
            >
              <CreditCardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No cards added yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add your first card to start optimizing your rewards
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenModal}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                }}
              >
                Add Your First Card
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {userCards.map((uc, index) => {
                const cardBackground = getCardBackground(uc.card);
                const cardPattern = getCardPattern(uc.card.card_type);
                const bankObj = uc.card.bank as Bank;
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={uc.id}>
                    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          height: 240,
                          cursor: 'pointer',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                            '& .card-details-overlay': {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        {/* Card Background with Pattern */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: cardBackground,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: cardPattern,
                              opacity: 0.4,
                            },
                          }}
                        />
                        
                        {/* Main Card Content (Visible by default) */}
                        <Box
                          sx={{
                            position: 'relative',
                            height: '100%',
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            color: 'white',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            transition: 'opacity 0.3s ease',
                            '.MuiCard-root:hover &': {
                              opacity: 0.1,
                            },
                          }}
                        >
                          {/* Top Section - Bank and Card Type */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'rgba(255,255,255,0.9)', 
                                  fontWeight: 600,
                                  letterSpacing: '0.5px',
                                  textTransform: 'uppercase',
                                  fontSize: '0.7rem',
                                }}
                              >
                                {bankObj?.name || 'Bank'}
                              </Typography>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '1rem',
                                  lineHeight: 1.2,
                                  mt: 0.5,
                                }}
                              >
                                {uc.card.name}
                              </Typography>
                            </Box>
                            <Chip
                              label={getCardLogo(uc.card.card_type)}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                fontWeight: 700,
                                backdropFilter: 'blur(4px)',
                                fontSize: '0.7rem',
                                height: 24,
                                border: '1px solid rgba(255,255,255,0.3)',
                              }}
                            />
                          </Box>

                          {/* Middle Section - Chip and Contactless */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 45,
                                height: 30,
                                bgcolor: 'rgba(255,215,0,0.4)',
                                borderRadius: 1.5,
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(255,255,255,0.4)',
                                position: 'relative',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: 20,
                                  height: 12,
                                  background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, transparent 70%)',
                                  borderRadius: 1,
                                },
                              }}
                            />
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                border: '2px solid rgba(255,255,255,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                              }}
                            >
                              📱
                            </Box>
                          </Box>

                          {/* Bottom Section - Card Number and Expiry */}
                          <Box>
                            <Typography 
                              variant="h6" 
                              className="card-number"
                              sx={{ 
                                fontFamily: 'monospace', 
                                letterSpacing: 3,
                                mb: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 500,
                              }}
                            >
                              •••• •••• •••• {uc.card_number_last4}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                                  VALID THRU
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                  {uc.expiry_date ? new Date(uc.expiry_date).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' }) : 'N/A'}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCard(uc.id);
                                }}
                                disabled={deleteCardMutation.isPending}
                                sx={{
                                  color: 'white',
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(4px)',
                                  '&:hover': { 
                                    bgcolor: 'error.main',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>

                        {/* Hover Details Overlay */}
                        <Box
                          className="card-details-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
                            backdropFilter: 'blur(4px)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            color: 'white',
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StarIcon sx={{ fontSize: 20 }} /> Card Benefits
                          </Typography>
                          
                          <Stack spacing={2} sx={{ flex: 1 }}>
                            {uc.card.cashback_rate && uc.card.cashback_rate > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ 
                                  width: 36, 
                                  height: 36, 
                                  borderRadius: 2, 
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <PaymentIcon sx={{ color: 'white', fontSize: 20 }} />
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Cashback Rate
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {uc.card.cashback_rate}% on all purchases
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            
                            {uc.card.reward_points_rate && uc.card.reward_points_rate > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ 
                                  width: 36, 
                                  height: 36, 
                                  borderRadius: 2, 
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <StarIcon sx={{ color: 'white', fontSize: 20 }} />
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Reward Points
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {uc.card.reward_points_rate}x points per $1
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                width: 36, 
                                height: 36, 
                                borderRadius: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                                <OfferIcon sx={{ color: 'white', fontSize: 20 }} />
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Annual Fee
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  TT${uc.card.annual_fee}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {uc.card.card_type && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ 
                                  width: 36, 
                                  height: 36, 
                                  borderRadius: 2, 
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <CreditCardIcon sx={{ color: 'white', fontSize: 20 }} />
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Card Type
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {uc.card.card_type}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Stack>

                          {(uc.card as any).scraping_url && (
                            <Button
                              size="small"
                              variant="outlined"
                              fullWidth
                              startIcon={<OpenInNewIcon />}
                              href={(uc.card as any).scraping_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                mt: 2,
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                  borderColor: 'white',
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                },
                              }}
                            >
                              View on Bank Site
                            </Button>
                          )}
                        </Box>
                      </Card>
                    </Zoom>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Add Card Modal */}
      <Modal
        open={addCardModalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.5)' },
        }}
      >
        <Fade in={addCardModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: 1000 },
              maxHeight: '90vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: '0 50px 100px rgba(0,0,0,0.5)',
              p: { xs: 3, md: 4 },
            }}
          >
            {/* Modal Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AddIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Add New Card
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Link your credit card to start tracking rewards
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseModal} sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Modal Content */}
            <Grid container spacing={3}>
              {/* Bank Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Bank</InputLabel>
                  <Select
                    value={selectedBank ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const bankId = raw === '' ? null : Number(raw);
                      setSelectedBank(bankId && bankId > 0 ? bankId : null);
                      setSelectedCard(null);
                      setCardNumberLastFour('');
                      setExpiry('');
                    }}
                    label="Select Bank"
                    sx={{ borderRadius: 2 }}
                  >
                    {filteredBanks.map((bank, idx) => {
                      const isVirtual = bank.id == null;
                      const menuValue = bank.id != null ? bank.id : -(idx + 1);
                      return (
                        <MenuItem key={`${bank.code}-${menuValue}`} value={menuValue} disabled={isVirtual}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <BankIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {bank.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {bank.operating_category && (
                                  <Chip
                                    label={operatingCategoryLabel(bank.operating_category)}
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: bank.operating_category === 'TRADITIONAL' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.18)',
                                      color: bank.operating_category === 'TRADITIONAL' ? '#065f46' : '#92400e',
                                    }}
                                  />
                                )}
                                {isVirtual && (
                                  <Chip label="Not scraped yet" size="small" variant="outlined" />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* Available Cards */}
              {selectedBank && (
                <Grid item xs={12}>
                  {cardsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : cardsError ? (
                    <Alert severity="error">Error loading cards</Alert>
                  ) : availableCards.length > 0 ? (
                    <>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                        Select a Card
                      </Typography>
                      <Grid container spacing={2}>
                        {availableCards.map((card) => {
                          const bankObj = card.bank as Bank;
                          return (
                            <Grid item xs={12} sm={6} key={card.id}>
                              <Card
                                onClick={() => setSelectedCard(card.id)}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: 3,
                                  border: selectedCard === card.id ? '3px solid #667eea' : '1px solid #e5e7eb',
                                  transition: 'all 0.3s ease',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  height: 180,
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 12px 24px rgba(102, 126, 234, 0.2)',
                                  },
                                }}
                              >
                                {/* Card Background */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: card.image_url 
                                      ? `url(${card.image_url})` 
                                      : bankGradients[bankObj?.code?.toUpperCase() || 'default'] || cardBackgrounds.default,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)',
                                    },
                                  }}
                                />
                                
                                {/* Card Content */}
                                <Box
                                  sx={{
                                    position: 'relative',
                                    height: '100%',
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    color: 'white',
                                  }}
                                >
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                      {bankObj?.name || 'Bank'}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5 }}>
                                      {card.name}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Chip
                                      label={card.card_type}
                                      size="small"
                                      sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 600,
                                      }}
                                    />
                                    {card.cashback_rate && card.cashback_rate > 0 && (
                                      <Chip
                                        label={`${card.cashback_rate}% Cashback`}
                                        size="small"
                                        sx={{
                                          bgcolor: '#10b981',
                                          color: 'white',
                                          fontWeight: 600,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  ) : (
                    <Alert severity="warning">No cards available for this bank</Alert>
                  )}
                </Grid>
              )}

              {/* Card Details Form */}
              {selectedCard && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} gutterBottom>
                    Enter Card Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last 4 Digits"
                        value={cardNumberLastFour}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setCardNumberLastFour(value);
                        }}
                        placeholder="1234"
                        inputProps={{ maxLength: 4 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>••••</Typography>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Expiry (MM/YY)"
                        value={expiry}
                        onChange={handleExpiryChange}
                        placeholder="MM/YY"
                        inputProps={{ maxLength: 5 }}
                        InputProps={{
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Enter month and year (e.g., 12/28). The slash will be added automatically.
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Modal Footer */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={handleCloseModal}
                variant="outlined"
                sx={{ borderRadius: 2, px: 4 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddCard}
                disabled={!selectedCard || !cardNumberLastFour || !expiry || addCardMutation.isPending}
                startIcon={addCardMutation.isPending ? <CircularProgress size={20} /> : <AddIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  px: 4,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  },
                }}
              >
                {addCardMutation.isPending ? 'Adding...' : 'Add Card'}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Cards;